/**
 * CE QUE L'ÉCRAN D'INVENTAIRE REND À L'ŒIL — règles pures, testables.
 *
 * UTILITÉ : les trois décisions d'affichage du module ont toutes la même signature de défaut — elles
 * transforment une ABSENCE en une VALEUR, et personne ne le voit. Un « — » devenu « 0 » se lit comme
 * de la démarque ; un écart « pas encore arrêté » rendu « 0,00 € » se lit comme un inventaire juste.
 */
import { formatMoney } from '@orgazone/money-format';
import { t } from '../i18n';

/**
 * Une quantité comptée — ou le tiret qui dit qu'elle ne l'a pas été.
 *
 * ⚠️ LE TIRET EST LA MOITIÉ QUI COMPTE. « — » dit « pas compté », « 0 » dit « rien trouvé ». Les
 * rendre pareil ferait lire, sur un inventaire à moitié saisi, une démarque totale sur tout ce qui
 * reste à voir.
 */
export function quantiteLisible(qty: number | null): string {
  return qty == null ? '—' : String(qty);
}

/**
 * Un écart valorisé — ou le tiret quand il n'est pas encore arrêté.
 *
 * ⚠️ `null` NE DEVIENT PAS 0,00 €. Une campagne close sans écart AFFIRME « rien ne manque » ; une
 * campagne qu'on n'a pas encore validée n'affirme rien du tout. Confondre les deux ferait annoncer un
 * inventaire juste à quelqu'un qui n'a rien compté.
 */
export function ecartLisible(cents: number | null): string {
  if (cents == null) return '—';
  const signe = cents > 0 ? '+' : cents < 0 ? '−' : '';
  return signe + formatMoney(Math.abs(cents), { thousandsSep: ' ' });
}

/**
 * LES TROIS ONGLETS D'UNE SESSION D'INVENTAIRE (maquette BO, `invTabs`).
 *
 * ⚠️ IL N'Y A PAS D'ONGLET « TOUT », ET C'EST VOLONTAIRE. Une référence comptée et conforme n'appelle
 * aucun geste : la lister ne fait que diluer les deux seules choses sur lesquelles on peut agir — ce
 * qui diffère, et ce qu'on n'a pas vu. Sur trente mille références, un onglet « tout » est l'endroit
 * où l'on ne trouve rien.
 */
export type VueRapprochement = 'zones' | 'ecarts' | 'noncomptees';

/**
 * Filtre les lignes du rapprochement selon la vue choisie.
 *
 * ⚠️ « NON COMPTÉES » EST UNE VUE À PART ENTIÈRE, pas un tri. C'est l'alerte n°1 du §6.3 — « devrait
 * être là mais pas scanné » : oubli de scan, carton resté en réserve, ou vraie démarque. Sur un
 * inventaire de trente mille références, la chercher à l'œil dans le tableau complet revient à ne pas
 * l'avoir.
 *
 * ⚠️ « ÉCARTS » NE MONTRE QUE DES LIGNES CONSTATÉES. Une référence non comptée a un écart nul faute
 * d'avoir été vue : la faire apparaître ici la ferait passer pour vérifiée et sans problème.
 */
export function filtrerRapprochement<T extends { countedQtyMinor: number | null; deltaQtyMinor: number }>(
  rows: readonly T[], vue: VueRapprochement,
): T[] {
  if (vue === 'noncomptees') return rows.filter((r) => r.countedQtyMinor == null);
  if (vue === 'ecarts') return rows.filter((r) => r.countedQtyMinor != null && r.deltaQtyMinor !== 0);
  return [...rows];
}


/**
 * L'ÉTAT D'UNE ZONE, tel que sa carte le dit.
 *
 * ⚠️ « COMPTAGE EN COURS » A UNE PÉREMPTION, et c'est la moitié qui compte. Une animation qui tourne
 * pour toujours AFFIRME une activité : un Mobile Partner tombé dans un carton il y a trois heures
 * continuerait d'annoncer qu'il compte. On aurait remplacé un chiffre faux par une présence fausse.
 * Passé le délai, la carte cesse de prétendre et dit ce qu'elle sait : quand la zone a été ouverte, et
 * que rien n'est arrivé depuis.
 *
 * ⚠️ AUCUN CHIFFRE PENDANT LE COMPTAGE, et ce n'est pas qu'une économie de ressources (décision
 * utilisateur 2026-09-02) : un décompte partiel serait FAUX À L'ÉCRAN. Une zone scannée à 30 %
 * afficherait « 42 pièces », et l'onglet Écarts montrerait un manque massif sur ses articles — un
 * artefact produit par l'affichage lui-même. Les points disent la seule chose vraie : ça compte, il
 * n'y a pas encore de chiffre.
 */
export type EtatZone = 'counting' | 'silent' | 'received';

/** Au-delà, une zone ouverte cesse d'être annoncée « en cours » : on ne sait plus si elle vit. */
export const SILENCE_ZONE_MS = 30 * 60_000;

export function etatZone(
  z: { open: boolean; openedAt: number | null; submittedAt: number | null },
  maintenant: number,
): EtatZone {
  if (!z.open) return 'received';
  if (z.openedAt != null && maintenant - z.openedAt > SILENCE_ZONE_MS) return 'silent';
  return 'counting';
}

/** Ce que la carte d'une zone AFFICHE à la place d'un décompte — jamais un nombre inventé. */
export function libelleEtatZone(etat: EtatZone, openedAt: number | null, maintenant: number): string {
  if (etat === 'received') return '';
  if (etat === 'counting') return t('Comptage en cours');
  const minutes = openedAt == null ? 0 : Math.round((maintenant - openedAt) / 60_000);
  const duree = minutes >= 120 ? `${Math.round(minutes / 60)} h` : `${minutes} min`;
  return t('Ouverte il y a {duree} — aucune nouvelle depuis', { duree });
}

/**
 * L'AVANCEMENT D'UNE SESSION : ce que le théorique annonçait, ce qui a été vu.
 *
 * ⚠️ LE RESTE N'EST PAS UNE PERTE, c'est ce qui n'a pas encore été regardé. La barre le montre en creux
 * — elle ne se remplit pas « vers 100 % de succès », elle consomme un total connu d'avance. Un
 * pourcentage seul ferait lire une progression de travail ; le couple vues/total dit ce qu'il reste.
 */
export function avancementSession(vues: number, total: number): { ratio: number; reste: number } {
  if (total <= 0) return { ratio: 0, reste: 0 };
  return { ratio: Math.min(1, vues / total), reste: Math.max(0, total - vues) };
}

/**
 * La vue de l'écran → celle que le SERVEUR comprend.
 *
 * ⚠️ LE FILTRAGE EST PARTI AU SERVEUR AVEC LA PAGINATION. Filtrer une PAGE donnerait « 3 écarts » sur
 * les deux cents lignes reçues alors qu'il y en a neuf cents ailleurs — un filtre qui ment d'autant
 * plus qu'il y a de données. `filtrerRapprochement` reste pour les jeux complets (tests, exports).
 */
export const VUE_SERVEUR: Record<VueRapprochement, string> = {
  // L'onglet Zones ne montre pas de table : on demande quand même le rapprochement, pour ses COMPTEURS
  // (qui sont globaux), mais une seule ligne suffit — voir `TAILLE_ZONES` à l'écran.
  zones: 'all',
  ecarts: 'variances',
  noncomptees: 'notcounted',
};

/**
 * LA SIGNATURE D'UNE ZONE — ce qui, en changeant, veut dire qu'un comptage a bougé.
 *
 * ⚠️ PAS SEULEMENT SON EXISTENCE. Un appareil qui pousse une SECONDE fois dans une zone déjà connue
 * change son contenu sans en créer une nouvelle : ne comparer que les identifiants raterait
 * précisément le cas où un comptage en écrase un autre.
 */
export function signatureZone(z: { open: boolean; lineCount: number; totalQtyMinor: number; attached: boolean }): string {
  return [z.open ? 'o' : 's', z.lineCount, z.totalQtyMinor, z.attached ? 'a' : '-'].join('|');
}

/**
 * Les zones d'APPAREIL qui ont changé depuis le dernier relevé.
 *
 * ⚠️ ELLE SE TAIT AU PREMIER RELEVÉ, par construction : sans mémoire, tout paraîtrait neuf et l'écran
 * annoncerait une arrivée pour chaque zone déjà là depuis une heure.
 * ⚠️ ELLE IGNORE LES ZONES BIPÉES AU CLAVIER (`fromBackOffice`) : c'est nous qui les remplissons, et
 * s'annoncer son propre geste n'apprend rien.
 * ⚠️ ELLE IGNORE UNE ZONE ENCORE OUVERTE SUR L'APPAREIL : elle compte hors ligne et n'a rien transmis,
 * donc rien à reporter aux compteurs (§23.0 — ne pas afficher un avancement qu'on n'a pas reçu).
 */
export function zonesArrivees<T extends {
  id: string; label: string; open: boolean; attached: boolean; fromBackOffice: boolean;
  lineCount: number; totalQtyMinor: number; equipmentCode?: string | null;
}>(recues: readonly T[], avant: ReadonlyMap<string, string> | null): { zone: T; connue: boolean }[] {
  if (avant == null) return [];
  const out: { zone: T; connue: boolean }[] = [];
  for (const z of recues) {
    if (z.fromBackOffice || z.open) continue;
    const sig = signatureZone(z);
    if (avant.get(z.id) === sig) continue;
    out.push({ zone: z, connue: avant.has(z.id) });
  }
  return out;
}

/**
 * QUAND UN APPAREIL A TRANSMIS — l'heure seule le jour même, la date au-delà.
 *
 * ⚠️ « À 09:41 » SUR UN ENVOI D'HIER EST UN MENSONGE TRANQUILLE. Un inventaire s'étale sur plusieurs
 * jours, et la page reste ouverte des heures : une heure nue ferait lire comme récent un comptage
 * vieux de deux jours, exactement au moment où l'on décide s'il faut recompter.
 *
 * ⚠️ ELLE NE DEVINE PAS. Sans horodatage, elle le DIT — jamais une heure de repli, qui serait une
 * donnée inventée (§23.0).
 */
export function heureDeReception(at: number | null, maintenant: number = Date.now()): string {
  if (at == null) return t('à une heure inconnue');
  const d = new Date(at);
  const h = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const n = new Date(maintenant);
  const memeJour = d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  if (memeJour) return `à ${h}`;
  return t('le {v}/{v2} à {h}', { v: String(d.getDate()).padStart(2, '0'), v2: String(d.getMonth() + 1).padStart(2, '0'), h });
}
